import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { FeaturePageShell } from "./FeaturePageShell";
import shot1 from "./assets/wizard/1.png";
import shot2 from "./assets/wizard/2.png";
import shot3 from "./assets/wizard/3.png";
import shot4 from "./assets/wizard/4.png";
import shot5 from "./assets/wizard/5.png";
import shot6 from "./assets/wizard/6.png";
import shot7 from "./assets/wizard/7.png";
import shot8 from "./assets/wizard/8.png";
import shot9 from "./assets/wizard/9.png";
import shot10 from "./assets/wizard/10.png";
import shot11 from "./assets/wizard/11.png";
import wizardVideo from "./assets/wizard/wizard.mp4";
import templateRelayImage from "./assets/vitrine/templates/tmplt_Relay.png";

const Phone = ({ src, caption }) => <figure className="fp-phoneCard"><img src={src} alt={caption} loading="lazy" /><figcaption>{caption}</figcaption></figure>;

const NaturalVideo = ({ src, className = "" }) => {
  const videoRef = useRef(null);

  const playWithSound = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  const keepPlayingMuted = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  return (
    <div className={`fp-naturalVideo ${className}`.trim()} onMouseEnter={playWithSound} onMouseLeave={keepPlayingMuted}>
      <video ref={videoRef} controls autoPlay muted loop playsInline preload="auto">
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
};

export default function WizardFeaturePage() {
  const heroVisual = (
    <div className="fp-wizardHeroPhones" aria-label="Weave Wizard mobile screens">
      <img src={shot1} alt="Start your weave menu" />
      <img src={shot4} alt="Weave Wizard layout selection" />
      <img src={shot10} alt="Weave Wizard contributor roles" />
    </div>
  );

  return (
    <FeaturePageShell
      pageTitle="Wizard"
      currentPath="/use-cases/wizard"
      eyebrow="WEAVE WIZARD"
      heroTitle="Design your Living Media in minutes."
      heroText="The Weave Wizard turns collaborative video production into a guided mobile workflow: build the blueprint, let AI structure the shot list, then invite contributors and launch."
      heroVisual={heroVisual}
      finalEyebrow="FROM SPARK TO SYNCHRONIZED CAPTURE"
      finalTitle="Frame the story, generate the slots and launch from your smartphone."
      finalText="Tap Start your weave, move through Blueprint, Slots and Settings, then publish the live link or save the thread at any stage."
    >
      <section className="fp-section fp-wizardGroup">
        <div className="fp-wizardGroupCopy">
          <span className="fp-stepNo">01 · BLUEPRINT</span>
          <h2>Lay the foundation in seconds.</h2>
          <p>Set the title and context, generate or upload a cover, then choose the narrative mechanics that fit the story.</p>
          <div className="fp-card"><span className="fp-cardLabel">6 turnkey layouts</span><p>Storyboard, Gallery, Symphony, Relay, Versus and Seed.</p></div>
        </div>
        <div className="fp-phoneGrid">
          <Phone src={shot1} caption="Start your weave from the central create action." />
          <Phone src={shot2} caption="Set the project title and editorial context." />
          <Phone src={shot3} caption="Generate an AI cover or upload your own visual." />
          <Phone src={shot4} caption="Select the narrative layout in one tap." />
        </div>
      </section>

      <section className="fp-section fp-wizardGroup">
        <div className="fp-wizardGroupCopy">
          <span className="fp-stepNo">02 · SLOTS</span>
          <h2>Let AI structure the shot list.</h2>
          <p>Choose Free or wallet-backed bounties, describe the filming mission and let Gemini draft shot briefs, framing guidance and target slots.</p>
          <div className="fp-card"><span className="fp-cardLabel">Precision briefs</span><p>Slots can include Google Places location tags, narrative direction, visual references and target durations from 3 to 60 seconds.</p></div>
        </div>
        <div className="fp-phoneGrid">
          <Phone src={shot5} caption="Choose the collaboration budget model." />
          <Phone src={shot6} caption="Toggle AI and describe the filming mission." />
          <Phone src={shot7} caption="Review the generated slot brief and location." />
          <Phone src={shot8} caption="Set duration and add more slots as needed." />
        </div>
      </section>

      <section className="fp-section fp-wizardGroup">
        <div className="fp-wizardGroupCopy">
          <span className="fp-stepNo">03 · SETTINGS</span>
          <h2>Invite, secure and deploy.</h2>
          <p>Keep the Weave private to an organization or open it to decentralized field contribution, then assign Weaver and Co-Creator roles before launch.</p>
          <div className="fp-card"><span className="fp-cardLabel">Instant launch</span><p>Start Weave publishes the live link and alerts invited collaborators. Save thread keeps the work safely staged before launch.</p></div>
        </div>
        <div className="fp-phoneGrid">
          <Phone src={shot9} caption="Choose visibility and invite collaborators." />
          <Phone src={shot10} caption="Assign Weaver or Co-Creator roles." />
          <Phone src={shot11} caption="Review the team and start the Weave." />
        </div>
      </section>

      <section className="fp-section fp-templatePromo">
        <div className="fp-templatePromoGrid">
          <div className="fp-templatePromoCopy">
            <h2>6 Collaborative Production Blueprints</h2>
            <p>Standardized scene templates designed to guide Weaver filming, unify raw footage, and streamline real-time sequence stitching.</p>
            <Link to="/template" className="hv2-inlineCta">Discover All 6 Frameworks →</Link>
          </div>
          <Link className="fp-templatePromoVisual" to="/template" aria-label="Discover All 6 Frameworks">
            <img src={templateRelayImage} alt="" loading="lazy" />
          </Link>
        </div>
      </section>

      <section className="fp-section">
        <div className="fp-sectionHead"><span className="fp-sectionEyebrow">FULL FLOW</span><h2>See the Wizard as one continuous mobile journey.</h2></div>
        <NaturalVideo src={wizardVideo} previewTime={5} className="fp-wizardVideo" />
      </section>
    </FeaturePageShell>
  );
}
